/**
 * PATH: apps/quote/src/pages/pricelist-page/PricelistPage.tsx
 * PURPOSE: Thin orchestrator for pricelist management with tabbed UI
 *
 * FLOW:
 *   ┌───────────────────────────────────────────────────────────────────┐
 *   │                         PRICELIST PAGE                            │
 *   ├─────────┬─────────┬──────────────┬─────────────┬─────────────────┤
 *   │ Items   │ Xero    │ Suppliers    │ Historical  │ AI Suggestions  │
 *   └─────────┴─────────┴──────────────┴─────────────┴─────────────────┘
 */

import { useState } from 'react';
import { Plus, Loader2, RefreshCw, AlertTriangle, Package, ExternalLink, FileSpreadsheet, History, Sparkles, Table2, ShoppingCart } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PriceBookItem, CreatePriceBookItemRequest } from '@/lib/quoteApi';
import {
  usePricelistData,
  usePricebookItems,
  useHistoricalSuggestions,
  useFilteredItems,
  type Tab,
} from './usePricelistData';
import ItemsTab from './ItemsTab';
import ItemEditorModal from './ItemEditorModal';
import HarvestWizardModal from './HarvestWizardModal';
import WorkbookTab from './WorkbookTab';
import XeroTab from './XeroTab';
import SuppliersTab from './SuppliersTab';
import HistoricalTab from './HistoricalTab';
import SuggestionsTab from './SuggestionsTab';
import MaterialsTab from './MaterialsTab';
import PivotTab from './PivotTab';

export default function PricelistPage() {
  const [activeTab, setActiveTab] = useState<Tab>('workbook');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPricebook, setSelectedPricebook] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [editor, setEditor] = useState<{ open: boolean; mode: 'create' | 'edit'; item: PriceBookItem | null }>({
    open: false, mode: 'create', item: null,
  });
  const [wizardOpen, setWizardOpen] = useState(false);

  const data = usePricelistData(activeTab);
  const activePricebookId =
    selectedPricebook || data.pricebooks.find((pb) => pb.is_default)?.id || data.pricebooks[0]?.id;

  const { items, itemsLoading } = usePricebookItems(activePricebookId);
  const { historicalSuggestions, suggestionsLoading } = useHistoricalSuggestions(activeTab, activePricebookId);
  const { categories, filteredItems } = useFilteredItems(items, searchQuery, categoryFilter);

  const saveItem = (payload: CreatePriceBookItemRequest, itemId?: string) => {
    if (!activePricebookId) return;
    const onDone = { onSuccess: () => setEditor((e) => ({ ...e, open: false })) };
    if (itemId) {
      data.updateItemMutation.mutate({ pricebookId: activePricebookId, itemId, data: payload }, onDone);
    } else {
      data.createItemMutation.mutate({ pricebookId: activePricebookId, data: payload }, onDone);
    }
  };
  const savingItem = data.createItemMutation.isPending || data.updateItemMutation.isPending;

  const tabs = [
    { id: 'workbook' as Tab, label: 'Pricelist (Excel)', icon: Table2 },
    { id: 'items' as Tab, label: 'Pricebook Items', icon: Package, count: items.length },
    { id: 'xero' as Tab, label: 'Xero Sync', icon: ExternalLink, badge: data.xeroStatus?.is_connected ? 'Connected' : undefined },
    { id: 'suppliers' as Tab, label: 'Supplier Quotes', icon: FileSpreadsheet, count: data.supplierQuotes.length },
    { id: 'historical' as Tab, label: 'From Quotes', icon: History, count: data.historicalData?.unique_items },
    { id: 'suggestions' as Tab, label: 'AI Suggestions', icon: Sparkles, count: historicalSuggestions?.count, highlight: (historicalSuggestions?.count || 0) > 0 },
    { id: 'materials' as Tab, label: 'Materials → PO', icon: ShoppingCart },
    { id: 'pivot' as Tab, label: 'Pivot', icon: Table2 },
  ];

  if (data.pricebooksLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (data.pricebooksError) {
    return (
      <div className="p-6 space-y-6">
        <PageHeader onAddItem={undefined} canAdd={false} />
        <ErrorBanner
          title="Failed to load pricebooks"
          message={data.pricebooksErrorData instanceof Error ? data.pricebooksErrorData.message : 'Unable to connect to pricing service'}
          onRetry={() => data.queryClient.invalidateQueries({ queryKey: ['pricebooks'] })}
        />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        canAdd={!!activePricebookId}
        onAddItem={() => setEditor({ open: true, mode: 'create', item: null })}
      />

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200 dark:border-gray-700 -mb-px overflow-x-auto">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm whitespace-nowrap transition-colors",
                isActive
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              )}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
              {tab.count !== undefined && tab.count > 0 && (
                <span className={cn(
                  "px-2 py-0.5 rounded-full text-xs",
                  tab.highlight ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                )}>
                  {tab.count}
                </span>
              )}
              {tab.badge && (
                <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {activeTab === 'items' && (
          <ItemsTab
            pricebooks={data.pricebooks}
            activePricebookId={activePricebookId}
            onSelectPricebook={setSelectedPricebook}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            categoryFilter={categoryFilter}
            onCategoryChange={setCategoryFilter}
            categories={categories}
            filteredItems={filteredItems}
            items={items}
            itemsLoading={itemsLoading}
            onDeleteItem={(pbId, itemId) => data.deleteItemMutation.mutate({ pricebookId: pbId, itemId })}
            onEditItem={(item) => setEditor({ open: true, mode: 'edit', item })}
          />
        )}

        {activeTab === 'workbook' && (
          <WorkbookTab
            pricebooks={data.pricebooks}
            activePricebookId={activePricebookId}
            onSelectPricebook={setSelectedPricebook}
          />
        )}

        {activeTab === 'xero' && (
          <XeroTab
            queryClient={data.queryClient}
            xeroStatus={data.xeroStatus}
            xeroStatusError={data.xeroStatusError}
            xeroStatusErrorData={data.xeroStatusErrorData}
            xeroContactsStatus={data.xeroContactsStatus}
            xeroSyncMutation={data.xeroSyncMutation}
            xeroContactsSyncMutation={data.xeroContactsSyncMutation}
          />
        )}

        {activeTab === 'suppliers' && (
          <SuppliersTab
            supplierQuotes={data.supplierQuotes}
            supplierQuotesLoading={data.supplierQuotesLoading}
          />
        )}

        {activeTab === 'historical' && (
          <HistoricalTab
            historicalData={data.historicalData}
            historicalLoading={data.historicalLoading}
            generatePricebookMutation={data.generatePricebookMutation}
            onOpenWizard={() => setWizardOpen(true)}
          />
        )}

        {activeTab === 'suggestions' && (
          <SuggestionsTab
            historicalSuggestions={historicalSuggestions}
            suggestionsLoading={suggestionsLoading}
            applyingItemId={data.applySuggestionMutation.isPending ? data.applySuggestionMutation.variables?.itemId : undefined}
            onApply={(itemId, suggestedPrice) => {
              if (activePricebookId) {
                data.applySuggestionMutation.mutate({ pricebookId: activePricebookId, itemId, suggestedPrice });
              }
            }}
          />
        )}

        {activeTab === 'materials' && <MaterialsTab />}

        {activeTab === 'pivot' && <PivotTab />}
      </div>

      <ItemEditorModal
        open={editor.open}
        mode={editor.mode}
        item={editor.item}
        pricebookId={activePricebookId}
        saving={savingItem}
        error={
          (data.createItemMutation.error as Error)?.message ||
          (data.updateItemMutation.error as Error)?.message ||
          null
        }
        onClose={() => setEditor((e) => ({ ...e, open: false }))}
        onSave={saveItem}
      />

      <HarvestWizardModal
        open={wizardOpen}
        pricebookId={activePricebookId}
        onClose={() => setWizardOpen(false)}
        onCommitted={() => {
          data.queryClient.invalidateQueries({ queryKey: ['pricebooks'] });
          data.queryClient.invalidateQueries({ queryKey: ['pricebook-items'] });
          setActiveTab('workbook');
        }}
      />
    </div>
  );
}

function PageHeader({ onAddItem, canAdd }: { onAddItem?: () => void; canAdd: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Pricelist Management</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Manage pricing from multiple sources: pricebooks, Xero, supplier quotes, and historical data
        </p>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={onAddItem}
          disabled={!canAdd || !onAddItem}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <Plus className="h-4 w-4" />
          Add Item
        </button>
      </div>
    </div>
  );
}

function ErrorBanner({ title, message, onRetry }: { title: string; message: string; onRetry: () => void }) {
  return (
    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
      <div className="flex items-center gap-3">
        <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
        <div>
          <h3 className="font-medium text-red-800 dark:text-red-200">{title}</h3>
          <p className="text-sm text-red-600 dark:text-red-400 mt-1">{message}</p>
        </div>
      </div>
      <button
        onClick={onRetry}
        className="mt-3 px-4 py-2 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded-md hover:bg-red-200 dark:hover:bg-red-900/60 transition-colors flex items-center gap-2"
      >
        <RefreshCw className="h-4 w-4" /> Retry
      </button>
    </div>
  );
}
